import useLanguage from '@/utils/hooks/useLanguage';
import { autoUpdate, flip, offset, useFloating } from '@floating-ui/react';
import { Menu } from '@headlessui/react';
import { ColumnDefTemplate, HeaderContext } from '@tanstack/react-table';
import cn from 'classnames';
import { saveAs } from 'file-saver';
import { FunctionComponent, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { BsFiletypeCsv, BsFiletypeXlsx } from 'react-icons/bs';
import { MdOutlineFileDownload, MdOutlineFileDownloadOff } from 'react-icons/md';
import * as XLSX from 'xlsx';
import { ContextMenuItem } from '../common/ContextMenu';
import Loader from './Loader';

interface ExportDataProps {
    className?: string;
    excelData?: {
        data: any[];
        fileName: string;
        headerData: (ColumnDefTemplate<HeaderContext<any, unknown>> | undefined)[];
    };
    fileName?: string;
    onDownloadData?: () => any;
    disabled?: boolean;
}

const ExportData: FunctionComponent<PropsWithChildren<ExportDataProps>> = (props) => {
    const { excelData, className, fileName = 'export', onDownloadData, disabled = false } = props;

    const lang = useLanguage();
    const [isCSVLoading, setIsCSVLoading] = useState(false);
    const [isXLSXLoading, setIsXLXSLoading] = useState(false);
    const [asyncData, setAsyncData] = useState<any>([]);

    const csvData = useMemo(() => (excelData ? [excelData?.headerData, ...(excelData?.data || [])] : []), [excelData]);
    const middleware = useMemo(() => [offset(12), flip()], []);

    const formattedCsvData = useMemo(() => {
        return csvData.map((row) => {
            return row.map((cell) => {
                if (typeof cell === 'string') {
                    // Replace commas with spaces
                    const sanitizedCell = cell.replace(/,/g, ' ');

                    if (!isNaN(parseFloat(sanitizedCell)) && sanitizedCell.includes('.')) {
                        // Properly wrap in double quotes
                        return `=""${sanitizedCell}""`;
                    }

                    return sanitizedCell;
                }

                // Handle numeric cells
                if (typeof cell === 'number') {
                    return cell.toString(); // Convert numbers to strings
                }

                return cell;
            });
        });
    }, [csvData]);

    const csvLink = useRef<any>(null);
    const { refs, floatingStyles } = useFloating({
        strategy: 'fixed',
        placement: 'bottom-end',
        whileElementsMounted: autoUpdate,
        middleware
    });

    const exportAsXLSX = useCallback(
        (exportData) => {
            const data = exportData.data.map((_, index) => {
                const value = {};
                excelData?.headerData.forEach((header, hid) => {
                    value[`${header}`] = exportData.data[index][hid];
                    return value;
                });
                return value;
            });
            const worksheet = XLSX.utils.json_to_sheet(data);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
            saveAs(blob, `${fileName}.xlsx`);
        },
        [excelData, fileName]
    );

    useEffect(() => {
        if (asyncData.length > 1) {
            csvLink.current.link.click();
        }
    }, [csvLink, asyncData]);

    return (excelData?.headerData || []).length < 2 ? (
        <span
            title="No data to export as CSV"
            className="w-auto flex items-center justify-center gap-1.5 text-xs font-medium py-2 px-3 rounded-md border border-brand-neutral-700 whitespace-nowrap">
            <MdOutlineFileDownloadOff className={cn(`w-4 h-4 text-white`, className)} />
            {lang.blotters.downloadData}
        </span>
    ) : (
        <Menu>
            <Menu.Button
                ref={refs.setReference}
                className={cn(
                    'flex hover:border-primary items-center justify-center gap-1.5 text-xs text-brand-text font-medium py-2 px-3 rounded-md border border-brand-neutral-700 hover:border-brand-neutral-500 whitespace-nowrap',
                    className
                )}>
                <MdOutlineFileDownload className="w-4 h-4" />
                {lang.blotters.downloadData}
            </Menu.Button>
            <Menu.Items
                ref={refs.setFloating}
                style={floatingStyles}
                className="z-10 divide-y divide-brand-neutral-700 rounded-md bg-brand-background-dark border border-brand-neutral-700 overflow-hidden">
                <Menu.Item>
                    <ContextMenuItem disabled={disabled || isXLSXLoading} noPadding>
                        {isXLSXLoading ? (
                            <div className="w-full">
                                <Loader className="w-4 h-4" />
                            </div>
                        ) : (
                            <div
                                onClick={async () => {
                                    setIsXLXSLoading(true);
                                    if (onDownloadData) {
                                        const res = await onDownloadData();
                                        exportAsXLSX({ data: res });
                                    } else exportAsXLSX(excelData);
                                    setIsXLXSLoading(false);
                                }}
                                className="flex gap-1.5 items-center justify-center text-xs py-2 pl-3 pr-4">
                                <BsFiletypeXlsx className="w-4 h-4" />
                                {lang.common.exportAsXLSXFile}
                            </div>
                        )}
                    </ContextMenuItem>
                </Menu.Item>
                <Menu.Item>
                    <ContextMenuItem disabled={disabled || isCSVLoading} noPadding>
                        {isCSVLoading ? (
                            <div className="w-full">
                                <Loader className="w-4 h-4" />
                            </div>
                        ) : (
                            <div>
                                {onDownloadData ? (
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            setIsCSVLoading(true);
                                            try {
                                                const res = await onDownloadData();
                                                const data = [excelData?.headerData, ...res];
                                                setAsyncData(data);
                                            } catch (e) {
                                                //
                                            }
                                            setIsCSVLoading(false);
                                        }}
                                        className="flex gap-1.5 items-center justify-center text-xs py-2 pl-3 pr-4">
                                        <BsFiletypeCsv className="w-4 h-4" />
                                        {lang.common.exportAsCSVFile}
                                    </button>
                                ) : (
                                    <CSVLink
                                        data={formattedCsvData}
                                        filename={`${fileName}.csv`}
                                        className="flex gap-1.5 items-center justify-center text-xs py-2 pl-3 pr-4">
                                        <BsFiletypeCsv className="w-4 h-4" />
                                        {lang.common.exportAsCSVFile}
                                    </CSVLink>
                                )}
                            </div>
                        )}
                        {onDownloadData && (
                            <CSVLink ref={csvLink} data={asyncData} filename={`${fileName}.csv`} className="hidden" />
                        )}
                    </ContextMenuItem>
                </Menu.Item>
            </Menu.Items>
        </Menu>
    );
};

export default ExportData;
